import { ReactElement, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Package } from 'lucide-react'

import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { Money } from '@/shared/components/ui'
import { type Product } from '@/lib/productService'

export interface ProductCardProps {
  product: Product
  highlighted?: boolean
}

export function ProductCard({ product, highlighted }: ProductCardProps): ReactElement {
  const navigate = useNavigate()
  const { t } = useTranslation('features-skus')

  const validVariants = useMemo(() => {
    return (
      product.variants?.filter(
        v =>
          v.archived !== true &&
          v.systemSku !== true &&
          !v.variantSkuKey?.startsWith('__') &&
          !v.name?.startsWith('__')
      ) ?? []
    )
  }, [product.variants])

  if (validVariants.length === 0) return <></>

  // Use product image or fallback to first variant image
  const imageUrl = product.imageUrl ?? validVariants[0]?.imageUrl
  const representativeSku = validVariants[0]
  const skuCount = validVariants.length
  
  // Calculate average cost or use representative
  const costs = validVariants
    .map(v => v.reportingCost)
    .filter((c): c is number => typeof c === 'number')
  
  const displayCost = costs.length > 0 
    ? costs.reduce((a, b) => a + b, 0) / costs.length 
    : null
    
  const currency = representativeSku?.reportingCurrency ?? 'USD'

  // Representative SKU code (slug)
  const displaySlug = representativeSku?.sku ?? ''

  return (
    <Card 
      className={`glass-card !p-0 group overflow-hidden cursor-pointer transition-all hover:border-primary/50 hover:shadow-md ${highlighted ? 'ring-2 ring-primary' : ''}`}
      onClick={() => navigate(`/dashboard/management/products/${product.id}`)}
    >
      <div className="aspect-square w-full overflow-hidden bg-muted/20 relative border-b border-border/50">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            className="h-full w-full object-cover transition-all duration-300 brightness-[0.7] group-hover:brightness-100"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground/40">
            <Package className="size-12" />
          </div>
        )}
      </div>
      
      <div 
        className="bg-black/10 cursor-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-4 pb-3 space-y-2">
          <h3 className="font-semibold text-sm leading-tight line-clamp-2 h-10" title={product.name}>
            {product.name}
          </h3>
          
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] text-muted-foreground font-mono truncate opacity-70 uppercase">
              {displaySlug}
            </p>
            {skuCount > 1 && (
              <span className="shrink-0 text-xs font-medium text-muted-foreground/70">
                {t('catalog.skuCount', { count: skuCount, defaultValue: `${skuCount} SKU` })}
              </span>
            )}
          </div>
        </CardContent>

        <div className="border-t border-border/40" />

        <CardFooter className="p-4 pt-3 flex items-center justify-between">
          <div className="font-semibold text-base">
            {displayCost !== null ? (
              <Money value={displayCost} currency={currency} />
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>
          <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
        </CardFooter>
      </div>
    </Card>
  )
}
